import { getAnalyticsConfig, shouldLoadAnalytics } from "./config"
import type { AnalyticsConfig } from "./config"
import { readConsentFromDocument } from "./consent"
import type { ConsentDecision } from "./consent"
import { beginCheckoutEvent, purchaseEvent } from "./events"
import type { AnalyticsEvent, AnalyticsItem } from "./events"
import { findGtagTag, resolveTags } from "./tags"
import type { AnalyticsTag } from "./tags"
import { createGtagSink } from "./vendors/gtag"

// window.dataLayer é o sink array-convenção neutro (consumido por GA4/GTM/Meta via tag).
// Declarar o tipo global senão o TS estrito falha no type-check ao aceder window.dataLayer.
// Tipado `unknown[]` (NÃO `AnalyticsEvent[]`): no cutover o tag do vendor (gtag/GTM) empurra
// `IArguments`/objetos de config que não são AnalyticsEvent — tipar mais estreito quebraria o
// snippet do vendor e falsificaria a promessa "só preencher a env, sem mudar código".
declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

// Sink: recebe um evento e o empurra ao destino. Injetável (testes usam um sink de array).
export type AnalyticsSink = (event: AnalyticsEvent) => void

export type EmitContext = {
  consent: ConsentDecision
  config: AnalyticsConfig
  sink: AnalyticsSink
}

/**
 * Fronteira de disparo, guardada pelo duplo gate. Retorna true se empurrou, false se no-op
 * silencioso (sem throw, sem tocar o sink quando o gate é falso).
 */
export function emitAnalyticsEvent(
  event: AnalyticsEvent,
  context: EmitContext
): boolean {
  if (!shouldLoadAnalytics(context.consent, context.config)) {
    return false
  }
  context.sink(event)
  return true
}

/**
 * Sink default: empurra ao window.dataLayer no browser. SSR-safe (guardado por typeof).
 * Cria o array on-demand — o tag do vendor (cutover) lê o mesmo array.
 */
export function defaultDataLayerSink(event: AnalyticsEvent): void {
  if (typeof window === "undefined") {
    return
  }
  // Array.isArray (não `?? []`): se algo pré-existente pôs window.dataLayer como não-array,
  // `.push` lançaria TypeError — recomeçamos de um array limpo em vez de estourar.
  const dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : []
  dataLayer.push(event)
  window.dataLayer = dataLayer
}

/**
 * Escolhe para onde o evento vai (Story 10.10). Com um tag gtag resolvido, o evento tem de
 * sair como COMANDO gtag — o `gtag.js` ignora objetos crus no dataLayer, que era a razão de
 * o scaffold da 2.6 nunca medir nada. Sem tag de vendor, mantém-se o sink neutro.
 *
 * Recebe os TAGS já resolvidos (e não a config): quem dispara um evento resolve-os uma vez e
 * reutiliza-os — resolver duas vezes por evento era trabalho repetido em cada disparo.
 */
export function resolveSink(tags: readonly AnalyticsTag[]): AnalyticsSink {
  const gtagTag = findGtagTag(tags)
  return gtagTag ? createGtagSink(gtagTag.vendorId) : defaultDataLayerSink
}

/**
 * Wrappers cliente: montam consent(document) + config(env) + o sink do vendor resolvido e
 * delegam ao núcleo guardado. Cada chamada relê consent/config frescos, por isso o gate
 * reflete o estado atual do cookie/env. No-op até haver provider configurado E consentimento.
 */
export function trackEvent(event: AnalyticsEvent): boolean {
  const config = getAnalyticsConfig()
  return emitAnalyticsEvent(event, {
    consent: readConsentFromDocument(),
    config,
    sink: resolveSink(resolveTags(config)),
  })
}

export function trackBeginCheckout(input: {
  currency: string
  itemId: string
  amountCents?: number
}): boolean {
  return trackEvent(beginCheckoutEvent(input))
}

export function trackConversion(input: {
  transactionId: string
  currency: string
  amountCents: number
  items?: AnalyticsItem[]
}): boolean {
  return trackEvent(purchaseEvent(input))
}
